import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Chip,
  CircularProgress,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import UpdateIcon from "@mui/icons-material/Update";
import styled from "styled-components";
import apiService from "../../modules/services/apiService";

const StatusPill = styled(Chip)`
  font-size: 0.8rem;
  height: 24px;
`;

const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  const d = new Date(dateString);
  return d.toLocaleString([], { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

export const CartStatusWidget = () => {
  const [loading, setLoading] = useState(true);
  const [itemsCount, setItemsCount] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetchCart = async () => {
      try {
        setLoading(true);
        const res = await apiService.getGroceryCart();
        const items = res?.data?.items || [];
        const updated = res?.data?.lastUpdated || null;
        if (!mounted) return;
        setItemsCount(items.length);
        setLastUpdated(updated);
      } catch (e) {
        if (!mounted) return;
        // Fallback: treat as empty cart
        setItemsCount(0);
        setLastUpdated(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchCart();
    return () => {
      mounted = false;
    };
  }, []);

  const statusColor = itemsCount > 0 ? "success" : "default";
  const statusLabel = itemsCount > 0 ? `${itemsCount} item${itemsCount === 1 ? "" : "s"}` : "Empty";

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={160}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="Cart Status" subheader="Your grocery cart overview" />
      <CardContent>
        <Box display="flex" alignItems="center" gap={2}>
          <ShoppingCartIcon color={itemsCount > 0 ? "success" : "action"} />
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {statusLabel}
          </Typography>
          <StatusPill color={statusColor} label={itemsCount > 0 ? "Items in cart" : "No items"} />
        </Box>
        <Box display="flex" alignItems="center" gap={1} mt={2}>
          <UpdateIcon fontSize="small" color="action" />
          <Typography variant="body2" color="textSecondary">
            Last updated: {formatDateTime(lastUpdated)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CartStatusWidget;
